// Dzinly API service for credit management and image generation

// API URL should be configurable based on environment
// Use the proxy for development to avoid CORS issues
const isDevelopment = (import.meta as any).env.DEV;
const API_BASE_URL = isDevelopment ? 'http://localhost:8080/ai-widget/dzinly' : 'https://api.tech.renovateai.app/ai-widget/dzinly';  // Production URL
const API_KEY = 'pk_ZQU9soOQ0pVW9r3MRdpmcQbo7btGc2dUGVMEsqj';

// Helper function to get the origin header for API requests
export function getOriginHeader(): string {
  try {
    // Get origin from URL parameter if available
    const urlParams = new URLSearchParams(window.location.search);
    const originParam = urlParams.get('origin');

    if (originParam) {
      return originParam;
    }
  } catch (error) {
    console.error('Error getting origin from URL parameter:', error);
  }

  return 'https://dzinly.in';
}

// Types for API responses
export interface CreditDetails {
  userId: string;
  designsLeft: number;
  totalDesigns: number;
  canDesign: boolean;
}

// Fetch user credit details
export async function getUserCredits(clientKey: string, identifier: { userId?: string }): Promise<any> {
  try {
    if (!identifier.userId) {
      throw new Error('User ID is required');
    }

    const response = await fetch(`${API_BASE_URL}/user/credits?user_id=${encodeURIComponent(identifier.userId)}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${clientKey}`,
        'Content-Type': 'application/json',
        'X-API-KEY': API_KEY,
        'X-CLIENT-KEY': clientKey,
        'X-ORIGIN': getOriginHeader()
      }
    });

    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }

    const data = await response.json();
    return data.credits?.result || data;
  } catch (error) {
    console.error('Error fetching user credits:', error);
    // Return fallback data
    return {
      userId: identifier.userId || '',
      designsLeft: 3,
      totalDesigns: 3,
      canDesign: true
    };
  }
}

// Consume a design credit
export async function consumeDesignCredit(apiKey: string, identifier: { userId?: string }): Promise<CreditDetails> {
  try {
    // This model matches what the backend expects
    const consumeDto = {
      user_id: Number(identifier.userId),
      style_ids: '', // Default empty string if not provided
      color_ids: '', // Default empty string if not provided
      inspirational_houses: [],
      keywords: '',
      extra: {
        project_id: 0,
        tmp_project_id: 0,
        view_id: 0,
        project_view_id: 0
      },
      renovated_image_url: ''
    };

    const response = await fetch(`${API_BASE_URL}/credits/consume`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${apiKey}`,
        'Content-Type': 'application/json',
        'X-API-KEY': API_KEY,
        'X-ORIGIN': getOriginHeader()
      },
      body: JSON.stringify(consumeDto)
    });

    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }

    const result = await response.json();
    return result.credits || result;
  } catch (error) {
    console.error('Error consuming design credit:', error);
    // Return fallback data for development/testing
    return {
      userId: identifier.userId || '',
      designsLeft: 2, // One less than default
      totalDesigns: 3,
      canDesign: true
    };
  }
}

// Renovate API Integration
// Types for Renovate API
export interface Asset {
  id: string;
  name: string;
  width: number;
  height: number;
  image_url: string;
  asset_id: string;
  preview_url?: string;
}

export interface AssetUploadResponse {
  asset: Asset;
}

export interface RenovateResponse {
  success: boolean;
  result_image_url: string;
  original_image_url: string;
  bubbles?: any[];
}

export type RenovationSpectrum = 'tweak' | 'enhance' | 'renovate' | 'transform';

export interface DetectedObject {
  originalObject: string; // Original object key name
  name: string; // readable object name
}

export interface DetectObjectsResponse {
  success: boolean;
  image_url: string;
  detected_objects: DetectedObject[];
}

export interface Bubble {
  bubbleId: string;
  bubbleType: string;
  contenType: 'text' | 'image';
  content?: any;
  status: 'empty' | 'filled' | 'loading' | 'locked';
}

interface SavedDesign {
  id: string
  name: string
  originalImageUrl: string
  generatedImageUrl: string
  timestamp: number
}

export interface WidgetConfigItem {
  id: number;
  code: string;
  name: string;
  image: string;
}

export interface DzinlyResultResponse {
  status: string;
  result: WidgetConfigItem[];
}

export interface InspirationalHouse {
  id: number;
  code: string;
  name: string;
  image: string;
}

export interface ColorFamilyWithHouses {
  id: number;
  code: string;
  name: string;
  image: string;
  inspirational_houses: InspirationalHouse[];
}

export interface WidgetConfigResponse {
  success: boolean;
  config: {
    styles: DzinlyResultResponse;
    colorFamiliesWithInspirationalHouses?: ColorFamilyWithHouses[];
    // Legacy fields for backward compatibility
    colorFamilies?: DzinlyResultResponse;
    inspirationalHouses?: DzinlyResultResponse;
    customInspirationImage?: string;
  };
  existingProjectDetails?: {
    savedRenovations?: SavedDesign[];
    initialUploadedAsset?: Asset | null;
    customInspirationImage?: string;
    inspirationImageUrls?: string[];
  }
}

const getApiUrlWithCurrentWindowParams = (url: string): string => {
   // Construct the URL with existing query parameters
   const newUrl = new URL(url);
   const urlParams = new URLSearchParams(window.location.search);


   for (const [key, value] of urlParams.entries()) {
     newUrl.searchParams.append(key, value);
   }

   return newUrl.toString();
};

// Function to upload an asset to the Dzinly API
export async function uploadAsset(imageFile: File, config?: { inspiration?: boolean }): Promise<Asset> {
  const formData = new FormData();
  formData.append('image', imageFile);

  if (config?.inspiration) {
    formData.append('inspiration', 'true');
  }

  try {
    const response = await fetch(getApiUrlWithCurrentWindowParams(`${API_BASE_URL}/assets/upload`), {
      method: 'POST',
      headers: {
        'X-API-KEY': API_KEY,
        'X-CLIENT-KEY': API_KEY,
        'X-ORIGIN': getOriginHeader()
      },
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`Asset upload failed: ${response.status} ${response.statusText}`);
    }

    const data = await response.json() as AssetUploadResponse;
    return data.asset;
  } catch (error) {
    console.error('Error uploading asset:', error);
    throw error;
  }
}

// Function to upload an asset using a URL
export async function uploadAssetFromUrl(imageUrl: string): Promise<Asset> {
  const formData = new FormData();
  formData.append('image_url', imageUrl);

  try {
    const response = await fetch(`${API_BASE_URL}/assets/upload`, {
      method: 'POST',
      headers: {
        'X-API-KEY': API_KEY,
        'X-CLIENT-KEY': API_KEY,
        'X-ORIGIN': getOriginHeader()
      },
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`Asset upload failed: ${response.status} ${response.statusText}`);
    }

    const data = await response.json() as AssetUploadResponse;
    return data.asset;
  } catch (error) {
    console.error('Error uploading asset from URL:', error);
    throw error;
  }
}

// Function to renovate an image
export async function renovateImage(
  assetId: string,
  styleId: string = 'modern',
  renovationSpectrum: RenovationSpectrum = 'renovate',
  preservedObjects: string[] = [],
  clientBubbles: any[] = [],
  default_renovation = false,
  user_id?: string,
  extra?: Record<string, string | number>,
  upscale_resolution: number = 3,
): Promise<RenovateResponse> {
  try {
    // Create the renovation data object
    const renovateDto = {
      asset_id: assetId,
      bubbles: clientBubbles,
      preserved_objects: preservedObjects,
      default_renovation: default_renovation,
      user_id: user_id || undefined,
      upscale_resolution: upscale_resolution,
      extra: extra || {
        project_id: 0,
        tmp_project_id: 0,
        view_id: 0,
        project_view_id: 0
      }
    };

    const response = await fetch(`${API_BASE_URL}/renovate`, {
      method: 'POST',
      headers: {
        'X-API-KEY': API_KEY,
        'X-CLIENT-KEY': API_KEY,
        'Content-Type': 'application/json',
        'X-ORIGIN': getOriginHeader()
      },
      body: JSON.stringify(renovateDto),
    });

    if (!response.ok) {
      throw new Error(`Renovation failed: ${response.status} ${response.statusText}`);
    }

    return await response.json() as RenovateResponse;
  } catch (error) {
    console.error('Error renovating image:', error);
    throw error;
  }
}

// Interface for saved design response
export interface SaveDesignResponse {
  success: boolean;
  renovation_id: string;
  message?: string;
}

// Function to save a design
export async function saveDesign(
  name: string,
  assetId: string,
  originalImageUrl: string,
  generatedImageUrl: string,
  preservedObjects: string[] = [],
  clientBubbles: any[] = [],
  userId?: string,
  clientKey?: string,
  extra?: Record<string, string | number>,
  renovation_id?: string,
): Promise<SaveDesignResponse> {
  try {
    // Create the save design data object
    const saveDesignDto = {
      name,
      asset_id: assetId,
      original_image_url: originalImageUrl,
      generated_image_url: generatedImageUrl,
      bubbles: clientBubbles,
      preserved_objects: preservedObjects,
      user_id: userId || undefined,
      extra: extra || {
        project_id: 0,
        tmp_project_id: 0,
        view_id: 0,
        project_view_id: 0
      },
      renovation_id: renovation_id
    };

    // Prepare design data for saving

    const response = await fetch(`${API_BASE_URL}/renovations/save`, {
      method: 'POST',
      headers: {
        'X-API-KEY': API_KEY || '',
        'X-CLIENT-KEY': clientKey || '',
        'Content-Type': 'application/json',
        'X-ORIGIN': getOriginHeader()
      },
      body: JSON.stringify(saveDesignDto),
    });

    if (!response.ok) {
      throw new Error(`Save design failed: ${response.status} ${response.statusText}`);
    }

    return await response.json() as SaveDesignResponse;
  } catch (error) {
    console.error('Error saving design:', error);
    throw error;
  }
}

// Interface for delete design response
export interface DeleteDesignResponse {
  success: boolean;
  message?: string;
}

// Function to delete a design
export async function deleteDesign(
  renovation_id: string,
  userId?: string,
  clientKey?: string,
): Promise<DeleteDesignResponse> {
  try {
    // Create the delete design data object
    const deleteDesignDto = {
      renovation_id,
      user_id: userId || undefined
    };

    const response = await fetch(`${API_BASE_URL}/renovations/delete`, {
      method: 'POST',
      headers: {
        'X-API-KEY': API_KEY || '',
        'X-CLIENT-KEY': clientKey || '',
        'Content-Type': 'application/json',
        'X-ORIGIN': getOriginHeader()
      },
      body: JSON.stringify(deleteDesignDto),
    });

    if (!response.ok) {
      throw new Error(`Delete design failed: ${response.status} ${response.statusText}`);
    }

    return await response.json() as DeleteDesignResponse;
  } catch (error) {
    console.error('Error deleting design:', error);
    throw error;
  }
}

// Function to detect objects in an image
export async function detectObjects(imageUrl: string): Promise<DetectedObject[]> {
  try {
    // Detecting objects in image

    const detectDto = {
      image_url: imageUrl
    };

    // Sending object detection request
    const response = await fetch(`${API_BASE_URL}/detect-objects`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-API-KEY': API_KEY,
        'X-ORIGIN': getOriginHeader()
      },
      body: JSON.stringify(detectDto),
    });

    // Object detection request completed

    if (!response.ok) {
      throw new Error(`Object detection failed: ${response.status} ${response.statusText}`);
    }

    const data = await response.json() as DetectObjectsResponse;
    // Objects detected successfully

    return data.detected_objects;
  } catch (error) {
    console.error('[DEV] Error detecting objects:', error);

    // For development, return mock objects if the API fails
    // Returning empty objects array as fallback
    return [];
  }
}

// Function to fetch widget configuration (styles, color families, and inspirational houses)
export async function getWidgetConfig(clientKey: string): Promise<WidgetConfigResponse> {
  try {
    // Fetching widget configuration

    // Construct the URL with existing query parameters
    const url = new URL(`${API_BASE_URL}/widget-config`);
    const urlParams = new URLSearchParams(window.location.search);


    for (const [key, value] of urlParams.entries()) {
      url.searchParams.append(key, value);
    }

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
      'Authorization': `Bearer ${clientKey}`,
      'Content-Type': 'application/json',
      'X-API-KEY': API_KEY,
      'X-CLIENT-KEY': clientKey,
      'X-ORIGIN': getOriginHeader()
      },
    });

    // Log response details for debugging
    // Widget config response received

    if (!response.ok) {
      throw new Error(`Failed to fetch widget configuration: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    // Widget config data processed



    return data as WidgetConfigResponse;
  } catch (error) {
    console.error('[DEV] Error fetching widget configuration:', error);

    // Get customInspirationImage from URL params for fallback data
    let customInspirationImage: string | undefined;
    const urlParams = new URLSearchParams(window.location.search);
    for (const [key, value] of urlParams.entries()) {
      if (key === 'extra[inspirational_custom_img]' || key === 'extra[inspirational_custom_image]') {
        customInspirationImage = value;
      }
    }

    // Return fallback data for development/testing
    const fallbackData = {
      success: true,
      config: {
        styles: {
          status: "OK",
          result: [
            {
              id: 1,
              code: "modern",
              name: "Modern",
              image: "https://dzinly.com/img/modern.jpg"
            },
            {
              id: 2,
              code: "craftsman",
              name: "Craftsman",
              image: "https://dzinly.com/img/craftsman.jpg"
            },
            {
              id: 3,
              code: "colonial",
              name: "Colonial",
              image: "https://dzinly.com/img/colonial.jpg"
            }
          ]
        },
        colorFamilies: {
          status: "OK",
          result: [
            {
              id: 1,
              code: "whites",
              name: "Whites",
              image: "https://dzinly.com/img/colors/whites.jpg"
            },
            {
              id: 2,
              code: "grays",
              name: "Grays",
              image: "https://dzinly.com/img/colors/grays.jpg"
            },
            {
              id: 3,
              code: "blues",
              name: "Blues",
              image: "https://dzinly.com/img/colors/blues.jpg"
            }
          ]
        },
        inspirationalHouses: {
          status: "OK",
          result: [
            {
              id: 1,
              code: "201",
              name: "Inspirational House 1",
              image: "https://dzinly.com/img/option-01.jpg"
            },
            {
              id: 2,
              code: "202",
              name: "Inspirational House 2",
              image: "https://dzinly.com/img/option-02.jpg"
            }
          ]
        },
        colorFamiliesWithInspirationalHouses: [
          {
            id: 1,
            code: "blacks",
            name: "Blacks",
            image: "https://dzinly.com/img/color-blacks.jpg",
            inspirational_houses: [
              {
                id: 101,
                code: "blacks_modern_villa",
                name: "Modern Black Villa",
                image: "https://images.unsplash.com/photo-1518780664697-55e3ad937233?q=80&w=300&auto=format&fit=crop"
              },
              {
                id: 102,
                code: "blacks_industrial_loft",
                name: "Industrial Black Loft",
                image: "https://images.unsplash.com/photo-1523217582562-09d0def993a6?q=80&w=300&auto=format&fit=crop"
              }
            ]
          },
          {
            id: 2,
            code: "greens",
            name: "Greens",
            image: "https://dzinly.com/img/color-greens.jpg",
            inspirational_houses: [
              {
                id: 201,
                code: "greens_cottage_garden",
                name: "Garden Cottage",
                image: "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?q=80&w=300&auto=format&fit=crop"
              },
              {
                id: 202,
                code: "greens_modern_farmhouse",
                name: "Modern Farmhouse",
                image: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?q=80&w=300&auto=format&fit=crop"
              }
            ]
          },
          {
            id: 3,
            code: "neutrals",
            name: "Neutrals",
            image: "https://dzinly.com/img/color-neutrals.jpg",
            inspirational_houses: [
              {
                id: 301,
                code: "neutrals_minimalist_home",
                name: "Minimalist Home",
                image: "https://images.unsplash.com/photo-1600047509807-ba8f99d2cdde?q=80&w=300&auto=format&fit=crop"
              },
              {
                id: 302,
                code: "neutrals_rustic_cabin",
                name: "Rustic Cabin",
                image: "https://images.unsplash.com/photo-1449824913935-59a10b8d2000?q=80&w=300&auto=format&fit=crop"
              }
            ]
          }
        ]
      },
      customInspirationImage: customInspirationImage
    };

    // Using fallback widget configuration data
    return fallbackData;
  }
}